import React, { useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Paper,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import FileUploadButton from "@/components/common/FileUploadButton";
import RichTextEditor from "@/components/common/RichTextEditor";
import { uploadMediaFiles, uploadToCloudinary } from "@/utils/cloudinaryUpload";
import useRichTextWithUpload from "@/hooks/useRichTextWithUpload";

// Validation schema
const validationSchema = Yup.object({
  questionNumber: Yup.number()
    .required("Question number is required")
    .positive("Must be a positive number")
    .integer("Must be an integer"),
  dialogueScript: Yup.string()
    .required("Dialogue script is required")
    .trim(),
  explanation: Yup.string().nullable(),
  audioUrl: Yup.string().url("Must be a valid URL").nullable(),
  imageUrl: Yup.string().url("Must be a valid URL").nullable(),
  blanks: Yup.array()
    .of(
      Yup.object({
        position: Yup.number().required().positive().integer(),
        correctAnswers: Yup.array()
          .of(Yup.string().trim())
          .min(1, "At least one correct answer is required")
          .test("has-non-empty", "At least one answer must not be empty", (arr) =>
            arr?.some((a) => a?.trim())
          ),
      })
    )
    .min(1, "At least one blank is required"),
  childQuestions: Yup.array()
    .of(
      Yup.object({
        number: Yup.number().required().positive().integer(),
        question: Yup.string().required("Question text is required").trim(),
        choices: Yup.array()
          .of(
            Yup.object({
              text: Yup.string().required("Choice text is required").trim(),
              value: Yup.string().required(),
              isCorrect: Yup.boolean(),
            })
          )
          .test(
            "has-correct",
            "Must have exactly one correct answer",
            (choices) => choices?.filter((c) => c.isCorrect).length === 1
          ),
      })
    )
    .min(1, "At least one comprehension question is required"),
});

const DialogueQuestionGroupForm = ({
  initialData = null,
  exerciseDay,
  section,
  partNumber,
  onSubmit,
  onCancel,
}) => {
  // Initial values
  const initialValues = useMemo(() => {
    if (initialData?.parentQuestion) {
      // Edit mode - load existing dialogue group data
      const { parentQuestion, childQuestions: children } = initialData;
      return {
        questionNumber: parentQuestion.number || "",
        dialogueScript: parentQuestion.dialogueScript || "",
        explanation: parentQuestion.dialogueExplanation || "",
        blanks: parentQuestion.blanks || [{ position: 1, correctAnswers: [""] }],
        audioUrl: parentQuestion.audioUrl || "",
        imageUrl: parentQuestion.imageUrl || "",
        audioFile: null,
        imageFile: null,
        childQuestions:
          children?.map((q) => ({
            _id: q._id,
            number: q.number,
            question: q.question,
            choices: q.choices || [
              { text: "", value: "A", isCorrect: false },
              { text: "", value: "B", isCorrect: false },
              { text: "", value: "C", isCorrect: false },
              { text: "", value: "D", isCorrect: false },
            ],
          })) || [],
      };
    }
    // Create mode - auto-fill question number
    return {
      questionNumber: initialData?.nextQuestionNumber || "",
      dialogueScript: "",
      explanation: "",
      blanks: [{ position: 1, correctAnswers: [""] }],
      audioUrl: "",
      imageUrl: "",
      audioFile: null,
      imageFile: null,
      childQuestions: [
        {
          number: 1,
          question: "",
          choices: [
            { text: "", value: "A", isCorrect: false },
            { text: "", value: "B", isCorrect: false },
            { text: "", value: "C", isCorrect: false },
            { text: "", value: "D", isCorrect: false },
          ],
        },
      ],
    };
  }, [initialData]);

  // Rich text hook for explanation
  const {
    description: explanation,
    handleDescriptionChange: handleExplanationChange,
    handleDescriptionStateChange: handleExplanationStateChange,
    handleContentImgChange: handleExplanationImgChange,
    processAndUploadImages: processExplanationImages,
  } = useRichTextWithUpload(initialValues.explanation);

  // Formik setup
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        // Process explanation with images
        const finalExplanation = await processExplanationImages();

        // Collect media files for upload
        const mediaFiles = {
          audio: values.audioFile,
          image: values.imageFile,
        };

        // Upload all media files together
        const uploadedUrls = await uploadMediaFiles(mediaFiles, `toeic/exercises/${exerciseDay}`);

        // Create FormData
        const formData = new FormData();

        // Prepare dialogueData
        const dialogueData = {
          questionNumber: parseInt(values.questionNumber),
          dialogueScript: values.dialogueScript,
          explanation: finalExplanation,
          blanks: values.blanks.map((b) => ({
            position: b.position,
            correctAnswers: b.correctAnswers.filter((a) => a.trim()),
          })),
        };

        // Append uploaded URLs or existing URLs
        if (uploadedUrls.audioUrl) {
          dialogueData.audioUrl = uploadedUrls.audioUrl;
        } else if (values.audioUrl?.trim()) {
          dialogueData.audioUrl = values.audioUrl.trim();
        }

        if (uploadedUrls.imageUrl) {
          dialogueData.imageUrl = uploadedUrls.imageUrl;
        } else if (values.imageUrl?.trim()) {
          dialogueData.imageUrl = values.imageUrl.trim();
        }

        formData.append("dialogueData", JSON.stringify(dialogueData));
        formData.append(
          "childQuestions",
          JSON.stringify(
            values.childQuestions.map((q) => ({
              _id: q._id,
              number: q.number,
              question: q.question,
              choices: q.choices,
            }))
          )
        );
        formData.append("exerciseDay", exerciseDay);
        formData.append("section", section);
        formData.append("partNumber", partNumber.toString());

        onSubmit(formData);
      } catch (error) {
        console.error("Failed to upload audio:", error);
        alert("Failed to upload audio file. Please try again.");
      }
    },
  });

  // Auto-detect blanks
  const detectBlanks = () => {
    const blankMatches = formik.values.dialogueScript.match(/\{\{(blank)?\d+\}\}/g);
    if (blankMatches) {
      const positions = [...new Set(blankMatches.map((m) => parseInt(m.match(/\d+/)[0])))];
      positions.sort((a, b) => a - b);
      const newBlanks = positions.map((pos) => {
        const existing = formik.values.blanks.find((b) => b.position === pos);
        return existing || { position: pos, correctAnswers: [""] };
      });
      formik.setFieldValue("blanks", newBlanks);
    }
  };

  // File handlers
  const handleAudioFileChange = (data) => {
    const file = data?.file || data;
    if (file) {
      formik.setFieldValue("audioFile", file);
      formik.setFieldValue("audioUrl", "");
    } else {
      formik.setFieldValue("audioFile", null);
    }
  };

  const handleImageFileChange = (data) => {
    const file = data?.file || data;
    if (file) {
      formik.setFieldValue("imageFile", file);
      formik.setFieldValue("imageUrl", "");
    } else {
      formik.setFieldValue("imageFile", null);
    }
  };

  return (
    <FormikProvider value={formik}>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            💡 Dialogue Question Group Format
          </Typography>
          <Typography variant="body2">
            Create a dialogue with fill-in-the-blanks + multiple-choice questions. Use{" "}
            <code>{"{{1}}"}</code>, <code>{"{{2}}"}</code> in the dialogue script.
          </Typography>
        </Alert>

        {/* Question Number Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            1. Question Number
          </Typography>
          <TextField
            fullWidth
            type="number"
            name="questionNumber"
            label="Question Number"
            value={formik.values.questionNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.questionNumber && Boolean(formik.errors.questionNumber)}
            helperText={formik.touched.questionNumber && formik.errors.questionNumber}
            placeholder="e.g., 1, 2, 3"
            sx={{ maxWidth: 200 }}
            required
          />
        </Paper>

        {/* Dialogue Script Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            2. Dialogue Script
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={8}
            name="dialogueScript"
            value={formik.values.dialogueScript}
            onChange={formik.handleChange}
            onBlur={(e) => {
              formik.handleBlur(e);
              detectBlanks();
            }}
            error={formik.touched.dialogueScript && Boolean(formik.errors.dialogueScript)}
            helperText={
              formik.touched.dialogueScript && formik.errors.dialogueScript
                ? formik.errors.dialogueScript
                : "Use {{1}}, {{2}}, etc. for blanks. Auto-detected on blur."
            }
            placeholder="W: Theo, have you {{1}} the schedule for the employee {{2}} next month?&#10;M: I've been really {{3}}, so I've just started {{4}} it."
            sx={{ mb: 2 }}
          />

          <Button variant="outlined" size="small" onClick={detectBlanks} sx={{ mb: 2 }}>
            Auto-Detect Blanks
          </Button>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Explanation (Optional)
            </Typography>
            <RichTextEditor
              value={explanation}
              onChange={handleExplanationChange}
              onRichtextStateChange={handleExplanationStateChange}
              onContentImgUpload={handleExplanationImgChange}
              placeholder="Add explanation for this dialogue (supports images, formatting, etc.)"
              minHeight="200px"
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Audio File (Optional)
              </Typography>
              <FileUploadButton
                onFileSelect={handleAudioFileChange}
                accept="audio/*"
                label={formik.values.audioFile?.name || "Upload Audio"}
                currentFile={formik.values.audioFile}
                buttonProps={{ variant: "outlined", fullWidth: true }}
                showPreview
                previewUrl={formik.values.audioUrl}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Image (Optional)
              </Typography>
              <FileUploadButton
                onFileSelect={handleImageFileChange}
                accept="image/*"
                label={formik.values.imageFile?.name || "Upload Image"}
                currentFile={formik.values.imageFile}
                buttonProps={{ variant: "outlined", fullWidth: true }}
                showPreview
                previewUrl={formik.values.imageUrl}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Blanks Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">3. Fill-in-the-Blank Answers</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const maxPos = Math.max(...formik.values.blanks.map((b) => b.position), 0);
                formik.setFieldValue("blanks", [
                  ...formik.values.blanks,
                  { position: maxPos + 1, correctAnswers: [""] },
                ]);
              }}
              size="small"
            >
              Add Blank
            </Button>
          </Box>
          {formik.touched.blanks && formik.errors.blanks && typeof formik.errors.blanks === "string" && (
            <FormHelperText error>{formik.errors.blanks}</FormHelperText>
          )}

          <FieldArray name="blanks">
            {({ remove }) =>
              formik.values.blanks.map((blank, bIdx) => (
                <Accordion key={bIdx} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>
                      Blank {blank.position} - {blank.correctAnswers.filter((a) => a?.trim()).length}{" "}
                      answer(s)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" gap={2} mb={2}>
                      <TextField
                        type="number"
                        label="Position"
                        value={blank.position}
                        onChange={(e) =>
                          formik.setFieldValue(`blanks.${bIdx}.position`, parseInt(e.target.value))
                        }
                        size="small"
                        sx={{ width: 100 }}
                      />
                      <Box flex={1}>
                        <FieldArray name={`blanks.${bIdx}.correctAnswers`}>
                          {({ remove: removeAnswer, push }) => (
                            <>
                              {blank.correctAnswers.map((answer, aIdx) => (
                                <Box key={aIdx} display="flex" gap={1} mb={1}>
                                  <TextField
                                    fullWidth
                                    value={answer}
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        `blanks.${bIdx}.correctAnswers.${aIdx}`,
                                        e.target.value
                                      )
                                    }
                                    placeholder="e.g., finished, completed"
                                    size="small"
                                  />
                                  {blank.correctAnswers.length > 1 && (
                                    <IconButton onClick={() => removeAnswer(aIdx)} color="error" size="small">
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Box>
                              ))}
                              <Button size="small" startIcon={<AddIcon />} onClick={() => push("")}>
                                Add Alternative
                              </Button>
                            </>
                          )}
                        </FieldArray>
                      </Box>
                    </Box>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(bIdx)}>
                      Remove Blank
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))
            }
          </FieldArray>
        </Paper>

        {/* Child Questions Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">4. Comprehension Questions</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const maxNum = Math.max(...formik.values.childQuestions.map((q) => q.number), 0);
                formik.setFieldValue("childQuestions", [
                  ...formik.values.childQuestions,
                  {
                    number: maxNum + 1,
                    question: "",
                    choices: [
                      { text: "", value: "A", isCorrect: false },
                      { text: "", value: "B", isCorrect: false },
                      { text: "", value: "C", isCorrect: false },
                      { text: "", value: "D", isCorrect: false },
                    ],
                  },
                ]);
              }}
              size="small"
            >
              Add Question
            </Button>
          </Box>
          {formik.touched.childQuestions &&
            formik.errors.childQuestions &&
            typeof formik.errors.childQuestions === "string" && (
              <FormHelperText error>{formik.errors.childQuestions}</FormHelperText>
            )}

          <FieldArray name="childQuestions">
            {({ remove }) =>
              formik.values.childQuestions.map((question, qIdx) => (
                <Accordion key={qIdx} defaultExpanded={qIdx === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>
                      Q{question.number}: {question.question || "(Empty)"}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Number"
                          value={question.number}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `childQuestions.${qIdx}.number`,
                              parseInt(e.target.value)
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <TextField
                          fullWidth
                          label="Question Text"
                          value={question.question}
                          onChange={(e) =>
                            formik.setFieldValue(`childQuestions.${qIdx}.question`, e.target.value)
                          }
                          error={
                            formik.touched.childQuestions?.[qIdx]?.question &&
                            Boolean(formik.errors.childQuestions?.[qIdx]?.question)
                          }
                          helperText={
                            formik.touched.childQuestions?.[qIdx]?.question &&
                            formik.errors.childQuestions?.[qIdx]?.question
                          }
                        />
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" gutterBottom>
                      Choices:
                    </Typography>
                    {question.choices.map((choice, cIdx) => (
                      <Box key={cIdx} display="flex" gap={1} mb={1} alignItems="center">
                        <Chip
                          label={choice.value}
                          color={choice.isCorrect ? "success" : "default"}
                          size="small"
                          sx={{ minWidth: 40 }}
                        />
                        <TextField
                          fullWidth
                          value={choice.text}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `childQuestions.${qIdx}.choices.${cIdx}.text`,
                              e.target.value
                            )
                          }
                          size="small"
                        />
                        <Button
                          variant={choice.isCorrect ? "contained" : "outlined"}
                          color={choice.isCorrect ? "success" : "primary"}
                          onClick={() => {
                            const updated = question.choices.map((c, i) => ({
                              ...c,
                              isCorrect: i === cIdx,
                            }));
                            formik.setFieldValue(`childQuestions.${qIdx}.choices`, updated);
                          }}
                          size="small"
                        >
                          {choice.isCorrect ? "✓" : "Set"}
                        </Button>
                      </Box>
                    ))}
                    {formik.touched.childQuestions?.[qIdx]?.choices &&
                      formik.errors.childQuestions?.[qIdx]?.choices &&
                      typeof formik.errors.childQuestions?.[qIdx]?.choices === "string" && (
                        <FormHelperText error>
                          {formik.errors.childQuestions[qIdx].choices}
                        </FormHelperText>
                      )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => remove(qIdx)}
                      sx={{ mt: 1 }}
                    >
                      Remove Question
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))
            }
          </FieldArray>
        </Paper>

        {/* Action Buttons */}
        <Box display="flex" gap={2} justifyContent="flex-end">
          <Button onClick={onCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {initialData ? "Update" : "Create"} Dialogue Group
          </Button>
        </Box>
      </Box>
    </FormikProvider>
  );
};

export default DialogueQuestionGroupForm;
